/**
 * User detail view component for instructor dashboard
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Alert } from '../ui/Alert';
import { useUserLabHistory } from '../../hooks/useInstructorQueries';
import { assignmentService } from '../../services/assignment';
import type { Assignment } from '../../types/assignment';

interface UserDetailViewProps {
  userId: number;
  onBack: () => void;
  selectedLabSlug?: string;
  onLabOptionsChange?: (options: { slug: string; label: string }[]) => void;
  allowFilters?: boolean;
}

export function UserDetailView({ userId, onBack, selectedLabSlug: externalSelectedLab, onLabOptionsChange, allowFilters = true }: UserDetailViewProps) {
  const navigate = useNavigate();

  // React Query hooks
  const { data: userHistory, isLoading: loading, error } = useUserLabHistory(userId, true, true);

  // Local state
  const [localError, setLocalError] = useState<string | null>(null);
  const [userAssignments, setUserAssignments] = useState<Assignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [internalLabSlug, setInternalLabSlug] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [runView, setRunView] = useState<'history' | 'issues' | 'all'>('history');

  // Combine React Query error with local errors
  const displayError = error || localError;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Load user assignments
  useEffect(() => {
    const loadUserAssignments = async () => {
      if (!userId) return;

      setAssignmentsLoading(true);
      try {
        const assignments = await assignmentService.getAssignments({
          user_id: userId,
          is_active: true,
        });
        setUserAssignments(assignments);
      } catch (assignmentError) {
        console.error('Failed to load user assignments:', assignmentError);
        setLocalError('Failed to load assigned labs');
      } finally {
        setAssignmentsLoading(false);
      }
    };

    loadUserAssignments();
  }, [userId]);

  const allRuns = useMemo(() => {
    if (!userHistory) {
      return [];
    }
    return userHistory.runs;
  }, [userHistory]);

  const historyRuns = useMemo(
    () => allRuns.filter((run) => run.state.toLowerCase() !== 'failed'),
    [allRuns]
  );

  const issueRuns = useMemo(
    () => allRuns.filter((run) => run.state.toLowerCase() === 'failed'),
    [allRuns]
  );

  const labOptions = useMemo(() => {
    if (!userHistory) {
      return [] as Array<{ slug: string; label: string }>;
    }

    const unique = new Map<string, string>();
    allRuns.forEach((run) => {
      const label = run.lab?.title || run.lab_id;
      if (!unique.has(run.lab_id)) {
        unique.set(run.lab_id, label);
      }
    });
    return Array.from(unique.entries()).map(([slug, label]) => ({ slug, label }));
  }, [userHistory, allRuns]);

  useEffect(() => {
    onLabOptionsChange?.(labOptions);
  }, [labOptions, onLabOptionsChange]);

  useEffect(() => {
    if (allowFilters) {
      setInternalLabSlug('');
    }
    setCreatedFrom('');
    setCreatedTo('');
    setRunView('history');
  }, [userId, allowFilters]);

  const labFilterValue = allowFilters ? internalLabSlug : externalSelectedLab ?? '';
  const hasActiveFilters = Boolean(labFilterValue || createdFrom || createdTo || runView !== 'history');

  const filteredRuns = useMemo(() => {
    if (!userHistory) {
      return [];
    }

    const fromDate = createdFrom ? new Date(createdFrom) : null;
    if (fromDate) {
      fromDate.setHours(0, 0, 0, 0);
    }

    const toDate = createdTo ? new Date(createdTo) : null;
    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    let runsForView = historyRuns;
    if (runView === 'issues') {
      runsForView = issueRuns;
    } else if (runView === 'all') {
      runsForView = allRuns;
    }

    return runsForView.filter((run) => {
      const runDate = new Date(run.created_at);
      if (labFilterValue && run.lab_id !== labFilterValue) {
        return false;
      }
      if (fromDate && runDate < fromDate) {
        return false;
      }
      if (toDate && runDate > toDate) {
        return false;
      }
      return true;
    });
  }, [userHistory, allRuns, historyRuns, issueRuns, runView, labFilterValue, createdFrom, createdTo]);

  const filteredGrades = useMemo(() => {
    if (!userHistory) {
      return [];
    }
    const filteredRunIds = new Set(filteredRuns.map((run) => run.id));

    return userHistory.grades.filter((grade) => filteredRunIds.has(grade.run_id));
  }, [userHistory, filteredRuns]);

  const labsAttempted = historyRuns.length;
  const labsCompleted = historyRuns.filter((run) => run.state.toLowerCase() === 'destroyed').length;
  const activeRuns = historyRuns.filter((run) => ['pending', 'provisioning', 'active', 'destroying'].includes(run.state.toLowerCase())).length;

  const handleResetFilters = () => {
    if (allowFilters) {
      setInternalLabSlug('');
    }
    setCreatedFrom('');
    setCreatedTo('');
    setRunView('history');
  };

  const handleUnassignFromUser = async (assignmentId: number, labTitle: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to unassign "${labTitle}" from ${userHistory?.user.display_name || userHistory?.user.username}?`
    );

    if (!confirmed) return;

    try {
      await assignmentService.removeAssignment(assignmentId);
      setUserAssignments((prev) => prev.filter((assignment) => assignment.id !== assignmentId));
    } catch (assignmentError) {
      setLocalError('Failed to remove assignment');
    }
  };

  const getRunBadgeVariant = (
    state: string
  ): 'success' | 'default' | 'primary' | 'warning' | 'danger' | 'info' => {
    switch (state.toLowerCase()) {
      case 'active':
        return 'info';
      case 'destroyed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'pending':
      case 'provisioning':
      case 'destroying':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getGradeBadgeColor = (
    score: number,
    totalPoints: number
  ): 'success' | 'default' | 'primary' | 'warning' | 'danger' | 'info' => {
    const percentage = (score / totalPoints) * 100;
    if (percentage >= 90) return 'success';
    if (percentage >= 80) return 'info';
    if (percentage >= 70) return 'warning';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !userHistory) {
    return (
      <Card className="p-6">
        <Alert variant="error" className="mb-4">
          {error instanceof Error ? error.message : String(error)}
        </Alert>
        <Button onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Card>
    );
  }

  if (!userHistory) {
    return (
      <Card className="p-6">
        <p className="text-gray-400">User not found</p>
        <Button onClick={onBack} className="mt-4">
          Back to Overview
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-detail-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="secondary"
            onClick={onBack}
            className="mb-4"
            data-testid="back-to-overview"
          >
            Back to Learners
          </Button>
          <h1 className="text-2xl font-bold text-white">
            {userHistory.user.display_name || userHistory.user.username}
          </h1>
          <p className="text-gray-400">
            {userHistory.user.username} | {userHistory.user.email} | {userHistory.user.role}
            {userHistory.user.cohort ? ` | ${userHistory.user.cohort}` : ''}
          </p>
          {userHistory.user.assigned_instructor && (
            <p className="text-sm text-gray-500 mt-1">
              Instructor: {userHistory.user.assigned_instructor.display_name || userHistory.user.assigned_instructor.username}
            </p>
          )}
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card className="p-6 text-center">
          <div className="text-2xl font-bold text-blue-400">
            {labsAttempted}
          </div>
          <div className="text-sm text-gray-400">Labs Attempted</div>
        </Card>
        <Card className="p-6 text-center">
          <div className="text-2xl font-bold text-green-400">
            {labsCompleted}
          </div>
          <div className="text-sm text-gray-400">Labs Completed</div>
        </Card>
        <Card className="p-6 text-center">
          <div className="text-2xl font-bold text-purple-400">
            {userHistory.average_score !== undefined && userHistory.average_score !== null
              ? `${userHistory.average_score.toFixed(1)}%`
              : 'N/A'}
          </div>
          <div className="text-sm text-gray-400">Average Score</div>
        </Card>
        <Card className="p-6 text-center">
          <div className="text-2xl font-bold text-orange-400">
            {activeRuns}
          </div>
          <div className="text-sm text-gray-400">Active Runs</div>
        </Card>
        <Card className="p-6 text-center">
          <div className="text-2xl font-bold text-red-400">
            {issueRuns.length}
          </div>
          <div className="text-sm text-gray-400">Issue Runs</div>
        </Card>
      </div>

      {/* Run History */}
      <Card className="overflow-hidden" data-testid="user-run-history">
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Run History ({filteredRuns.length})
              </h2>
              <p className="text-sm text-gray-400">
                Review labs launched by this learner and filter by lab or date.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="run-view-filter">
                  View
                </label>
                <select
                  id="run-view-filter"
                  value={runView}
                  onChange={(event) => setRunView(event.target.value as 'history' | 'issues' | 'all')}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white min-w-[150px]"
                >
                  <option value="history">History</option>
                  <option value="issues">Issues</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="lab-filter">
                  Lab
                </label>
                <select
                  id="lab-filter"
                  value={labFilterValue}
                  onChange={(event) => allowFilters ? setInternalLabSlug(event.target.value) : undefined}
                  disabled={!allowFilters}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white min-w-[180px] disabled:opacity-50"
                >
                  <option value="">All labs</option>
                  {labOptions.map((option) => (
                    <option key={option.slug} value={option.slug}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!allowFilters && (
                  <p className="mt-1 text-xs text-gray-500">Use the lab dropdown above to change this selection.</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="created-from">
                  Created from
                </label>
                <input
                  id="created-from"
                  type="date"
                  value={createdFrom}
                  onChange={(event) => setCreatedFrom(event.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1" htmlFor="created-to">
                  Created to
                </label>
                <input
                  id="created-to"
                  type="date"
                  value={createdTo}
                  onChange={(event) => setCreatedTo(event.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Lab
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Latest Grade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredRuns.map((run) => (
                <tr key={run.id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">
                      {run.lab?.title || run.lab_id}
                    </div>
                    <div className="text-xs text-gray-400">{run.lab_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={getRunBadgeVariant(run.state)}>
                      {run.state.toLowerCase()}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(run.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(run.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {run.latest_grade ? (
                      <Badge variant={getGradeBadgeColor(run.latest_grade.score, run.latest_grade.total_points)}>
                        {run.latest_grade.score}/{run.latest_grade.total_points} ({run.latest_grade.percentage.toFixed(1)}%)
                      </Badge>
                    ) : (
                      <span className="text-gray-500">Not graded</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/runs/${run.id}`)}
                        data-testid={`view-run-${run.id}`}
                      >
                        View Run →
                      </Button>
                      {run.latest_grade && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/runs/${run.id}/grade`)}
                          data-testid={`view-run-report-${run.id}`}
                        >
                          Report →
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRuns.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              No lab runs match the selected filters.
            </div>
          )}
        </div>
      </Card>

      {/* Assigned Labs Section */}
      <Card className="overflow-hidden" data-testid="assigned-labs-section">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">
              Assigned Labs ({userAssignments?.length || 0})
            </h2>
            {assignmentsLoading && <LoadingSpinner size="sm" />}
          </div>
        </div>

        {(userAssignments?.length || 0) === 0 ? (
          <div className="p-6 text-center text-gray-400">
            {assignmentsLoading ? 'Loading assigned labs...' : 'No active lab assignments found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {(userAssignments || []).map((assignment) => (
              <div
                key={assignment.id}
                className="p-6 hover:bg-gray-800/30 transition-colors"
                data-testid={`assigned-lab-${assignment.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-medium">
                        {assignment.lab?.title}
                      </h3>
                      <Badge variant="info" size="sm">
                        {assignment.lab?.difficulty}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-400 space-y-1">
                      <div>Lab ID: {assignment.lab?.slug}</div>
                      <div>
                        Assigned: {new Date(assignment.created_at).toLocaleDateString()} by {assignment.assigned_by?.username}
                      </div>
                      <div>Status: {assignment.is_active ? 'Active' : 'Completed'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleUnassignFromUser(assignment.id, assignment.lab?.title || 'Unknown Lab')}
                      variant="secondary"
                      size="sm"
                      data-testid={`unassign-from-user-${assignment.id}`}
                    >
                      Unassign
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {displayError && (
        <Alert variant="error" className="mb-4">
          {displayError instanceof Error ? displayError.message : String(displayError)}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLocalError(null)}
            className="ml-4"
          >
            Dismiss
          </Button>
        </Alert>
      )}

      {/* Grades */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Grade History ({filteredGrades.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Lab
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Graded
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredGrades.map((grade) => {
                const currentScore =
                  grade.overrides.length > 0
                    ? grade.overrides[grade.overrides.length - 1].new_score
                    : grade.score;

                return (
                  <tr key={grade.id} className="hover:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {grade.run?.lab?.title || grade.run?.lab_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Badge variant={getGradeBadgeColor(currentScore, grade.total_points)}>
                          {currentScore}/{grade.total_points} ({((currentScore / grade.total_points) * 100).toFixed(1)}%)
                        </Badge>
                        {grade.overrides.length > 0 && (
                          <span className="text-xs text-yellow-400">
                            (Original: {grade.score})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(grade.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/runs/${grade.run_id}/grade`)}
                          data-testid={`view-detailed-report-${grade.id}`}
                        >
                          View Report →
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredGrades.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              No grades found for the selected filters.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
